import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Check, Clock, Copy, Image as ImageIcon, Trash2, Loader2 } from "lucide-react";
import { WEEKDAYS, normaliseWeek } from "@/lib/businessHours";
import { trackCropperEvent } from "@/lib/analytics";
import CoverPhotoCropper from "@/components/CoverPhotoCropper";
import AddressPinInput from "@/components/AddressPinInput";

export default function Profile() {
  const { user, updateProfile } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    business_name: user?.business_name || "",
    address: user?.address || "",
    phone: user?.phone || "",
    service_categories: user?.service_categories || [],
    business_hours: normaliseWeek(user?.business_hours),
    digest_frequency: user?.digest_frequency || "daily",
    bio: user?.bio || "",
    cover_photo_id: user?.cover_photo_id || "",
  });
  const [allCategories, setAllCategories] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [uploadingCover, setUploadingCover] = useState(false);
  // When the user picks a file we store its data: URL here so the
  // cropper modal can render it. `null` keeps the modal closed.
  const [cropSrc, setCropSrc] = useState(null);
  const BIO_MAX = 400;
  // Resolve the backend origin from REACT_APP_BACKEND_URL so the cover-
  // photo preview works in both preview and production without hardcoding.
  const fileBaseUrl =
    (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api/files";

  // Load the canonical list of service types so we don't hardcode.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/service-types");
        if (!cancelled) setAllCategories(data?.types || []);
      } catch {
        // Non-blocking; if this fails the checkboxes just won't render and
        // the user can still save the rest of the form.
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const toggleCategory = (cat) => {
    setForm((f) => {
      const has = f.service_categories.includes(cat);
      return {
        ...f,
        service_categories: has
          ? f.service_categories.filter((c) => c !== cat)
          : [...f.service_categories, cat],
      };
    });
  };

  const setDay = (dayKey, patch) => {
    setForm((f) => ({
      ...f,
      business_hours: {
        ...f.business_hours,
        [dayKey]: { ...f.business_hours[dayKey], ...patch },
      },
    }));
  };

  // "Copy Mon → weekdays" — one-tap setup so a business doesn't have to
  // hand-fill five identical rows. Weekend rows are left alone.
  const copyMonToWeekdays = () => {
    setForm((f) => {
      const mon = f.business_hours.mon;
      const next = { ...f.business_hours };
      for (const day of ["tue", "wed", "thu", "fri"]) {
        next[day] = { ...mon };
      }
      return { ...f, business_hours: next };
    });
    toast.success("Copied Monday to Tue–Fri");
  };

  // Same one-tap idea for 7-day operations (mobile studios, spray tan vans).
  // Only touches Sat + Sun so a business can layer this on top of the
  // weekday copy for a full same-hours-all-week schedule.
  const copyMonToWeekend = () => {
    setForm((f) => {
      const mon = f.business_hours.mon;
      return {
        ...f,
        business_hours: {
          ...f.business_hours,
          sat: { ...mon },
          sun: { ...mon },
        },
      };
    });
    toast.success("Copied Monday to Sat & Sun");
  };

  const uploadCover = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      toast.error("Pick an image file (JPG, PNG, WebP).");
      return;
    }
    // Source-file cap generous (10MB) because the crop step will
    // down-size to 1600px wide and recompress to JPEG — the UPLOADED
    // artefact is what has to stay under 5MB, not the source.
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Source image must be under 10MB.");
      return;
    }
    // Read into a data: URL so react-easy-crop can render it. Do NOT
    // upload yet — the cropper modal will hand us a 16:9 Blob to upload.
    const reader = new FileReader();
    reader.onload = () => {
      setCropSrc(reader.result);
      // Funnel event: user picked a file and the modal is about to mount.
      // Pairs with "confirmed" / "cancelled" to measure drop-off at the
      // crop step.
      trackCropperEvent("opened");
    };
    reader.onerror = () => toast.error("Couldn't read that image.");
    reader.readAsDataURL(file);
  };

  const handleCropComplete = async (blob) => {
    setUploadingCover(true);
    try {
      // The <input type="file"> API expects a File, but FastAPI's
      // UploadFile accepts any multipart part — we wrap the Blob in a
      // File so the filename lands cleanly in the server log.
      const file = new File([blob], "cover.jpg", { type: "image/jpeg" });
      const fd = new FormData();
      fd.append("files", file);
      const { data } = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const uploaded = data?.files?.[0];
      if (!uploaded?.id) throw new Error("Upload failed");
      setForm((f) => ({ ...f, cover_photo_id: uploaded.id }));
      setCropSrc(null);
      trackCropperEvent("upload_succeeded");
      toast.success("Cover photo cropped — don't forget to Save.");
    } catch (e) {
      trackCropperEvent("upload_failed");
      toast.error(
        e?.response?.data?.detail || e?.message || "Couldn't upload that image."
      );
    } finally {
      setUploadingCover(false);
    }
  };

  const clearCover = () => {
    setForm((f) => ({ ...f, cover_photo_id: "" }));
    toast.info("Cover removed. Save to apply.");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const r = await updateProfile(form);
    setBusy(false);
    if (!r.ok) {
      setError(r.error);
      toast.error(r.error);
      return;
    }
    toast.success("Profile updated");
    navigate("/dashboard");
  };

  return (
    <div className="mx-auto max-w-[720px] p-4 md:p-8">
      {cropSrc && (
        <CoverPhotoCropper
          imageSrc={cropSrc}
          onCropComplete={handleCropComplete}
          onCancel={() => setCropSrc(null)}
        />
      )}
      <p className="mono text-[10px] uppercase tracking-[0.3em] text-[#C9A85F]">
        → business profile
      </p>
      <h1 className="mt-2 text-4xl leading-tight tracking-tight md:text-5xl">
        Your business.
      </h1>
      <p className="mt-3 max-w-lg text-sm text-muted-foreground">
        We geocode your address so the dashboard map centers on your business.
        Pick every service you offer — customers see you under each of those
        filters on the map.
      </p>

      <form
        onSubmit={submit}
        // noValidate: delegate ALL form validation to the server. HTML5
        // `<input type="time">` holds a `validity.badInput=true` state
        // when the user types a partial time (e.g. "09" without the
        // minutes) that React can't observe or clear via the `value`
        // prop — so a day marked "Closed" with a leftover partial time
        // would silently block Save with Chrome's tooltip. The backend's
        // `clean_business_hours` already normalises any non-HH:MM string
        // to "", so letting a partial reach it is safe + reconciles the
        // state on the next refresh.
        noValidate
        data-testid="profile-form"
        className="mt-8 border border-border p-6 md:p-8 grid gap-5"
      >
        <div className="grid gap-1.5">
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">
            Business name
          </Label>
          <Input
            data-testid="profile-business-name-input"
            value={form.business_name}
            onChange={upd("business_name")}
            className="rounded-none"
          />
        </div>

        {/* Cover photo — hero image on the mobile business profile page */}
        <div className="grid gap-2" data-testid="profile-cover-photo-section">
          <Label className="mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
            <ImageIcon size={12} className="text-[#C9A85F]" /> Cover photo
          </Label>
          <p className="mono text-[10px] text-muted-foreground">
            Shown as the hero image on your business page in the Book Away
            app. You'll crop it to 16:9 after choosing. Source up to 10MB.
          </p>
          {form.cover_photo_id ? (
            <div className="relative mt-2 border border-border">
              <img
                src={`${fileBaseUrl}/${form.cover_photo_id}`}
                alt="Current cover"
                data-testid="profile-cover-photo-preview"
                className="block w-full h-48 object-cover"
              />
              <button
                type="button"
                data-testid="profile-cover-photo-remove"
                onClick={clearCover}
                className="absolute top-2 right-2 inline-flex items-center gap-1.5 bg-background/90 border border-border px-2.5 py-1 text-[10px] mono uppercase tracking-widest hover:border-[#B8422A] hover:text-[#B8422A] transition-colors"
              >
                <Trash2 size={11} /> Remove
              </button>
            </div>
          ) : (
            <label
              data-testid="profile-cover-photo-dropzone"
              className="mt-2 flex h-48 cursor-pointer items-center justify-center border border-dashed border-border bg-muted/20 hover:border-[#C9A85F] hover:bg-[#C9A85F]/5 transition-colors"
            >
              <input
                type="file"
                accept="image/*"
                data-testid="profile-cover-photo-input"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadCover(f);
                  // Reset so re-picking the same file still fires onChange.
                  e.target.value = "";
                }}
                className="hidden"
              />
              <div className="flex flex-col items-center gap-2 text-muted-foreground">
                {uploadingCover ? (
                  <>
                    <Loader2 size={20} className="animate-spin text-[#C9A85F]" />
                    <span className="mono text-[10px] uppercase tracking-widest">
                      Uploading…
                    </span>
                  </>
                ) : (
                  <>
                    <ImageIcon size={20} className="text-[#C9A85F]" />
                    <span className="mono text-[10px] uppercase tracking-widest">
                      Click to upload
                    </span>
                  </>
                )}
              </div>
            </label>
          )}
        </div>

        {/* Short bio — plain-text intro paragraph */}
        <div className="grid gap-1.5" data-testid="profile-bio-section">
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">
            Short bio
          </Label>
          <Textarea
            data-testid="profile-bio-input"
            value={form.bio}
            onChange={upd("bio")}
            maxLength={BIO_MAX}
            rows={4}
            placeholder="e.g. Family-run brow + lash studio in Surry Hills, open 7 days. Walk-ins welcome between appointments."
            className="rounded-none resize-none"
          />
          <div className="flex items-center justify-between">
            <p className="mono text-[10px] text-muted-foreground">
              Shown on your business page in the app. Keep it to a sentence
              or two — what you do, where you're based.
            </p>
            <span
              data-testid="profile-bio-charcount"
              className={`mono text-[10px] ${
                form.bio.length > BIO_MAX - 40
                  ? "text-[#B8422A]"
                  : "text-muted-foreground"
              }`}
            >
              {form.bio.length}/{BIO_MAX}
            </span>
          </div>
        </div>

        <div className="grid gap-1.5">
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">
            Business address
          </Label>
          <AddressPinInput
            testIdPrefix="profile-address"
            value={form.address}
            onChange={(v) => setForm((f) => ({ ...f, address: v }))}
            required
            placeholder="e.g. 500 Crown St, Surry Hills, Sydney"
            initialPinned={
              user?.lat != null && user?.lng != null && user?.address
                ? {
                    lat: user.lat,
                    lng: user.lng,
                    display_name: user.address,
                  }
                : null
            }
          />
          <p className="mono text-[10px] text-muted-foreground">
            Street + suburb + city works best. We use OpenStreetMap to geocode.
          </p>
        </div>

        <div className="grid gap-1.5">
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">
            Mobile number
          </Label>
          <Input
            data-testid="profile-phone-input"
            value={form.phone}
            onChange={upd("phone")}
            type="tel"
            required
            className="rounded-none"
            placeholder="+61412345678"
          />
          <p className="mono text-[10px] text-muted-foreground">
            International format (starts with +). Shown to customers once
            you approve their booking.
          </p>
        </div>

        {allCategories.length > 0 && (
          <div className="grid gap-2">
            <Label className="mono text-[10px] uppercase tracking-[0.2em]">
              Services you offer
            </Label>
            <p className="mono text-[10px] text-muted-foreground">
              Pick any that apply. A skin clinic offering injectables can
              select both — you'll appear under both filters on the map.
            </p>
            <div
              className="mt-1 flex flex-wrap gap-2"
              data-testid="profile-service-categories"
            >
              {allCategories.map((cat) => {
                const active = form.service_categories.includes(cat);
                return (
                  <button
                    type="button"
                    key={cat}
                    onClick={() => toggleCategory(cat)}
                    data-testid={`profile-category-${cat.replace(/\s+/g, "-").toLowerCase()}`}
                    aria-pressed={active}
                    className={
                      "mono inline-flex items-center gap-1.5 border px-3 py-1.5 text-[11px] uppercase tracking-[0.2em] transition-colors " +
                      (active
                        ? "border-foreground bg-foreground text-background"
                        : "border-border text-foreground hover:border-foreground")
                    }
                  >
                    {active && <Check size={12} />}
                    {cat}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div className="grid gap-2" data-testid="profile-hours-section">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <Label className="mono text-[10px] uppercase tracking-[0.2em] flex items-center gap-2">
                <Clock size={12} className="text-[#C9A85F]" /> Weekly opening hours
              </Label>
              <p className="mono text-[10px] text-muted-foreground mt-1">
                Consumers see today's status ("Open until 6pm") on each opening
                card, and the full week on your scanned-QR profile page.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                data-testid="hours-copy-mon-btn"
                onClick={copyMonToWeekdays}
                className="mono inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:border-[#C9A85F] hover:text-[#8A7333] transition-colors"
              >
                <Copy size={11} /> Copy Mon → Tue–Fri
              </button>
              <button
                type="button"
                data-testid="hours-copy-weekend-btn"
                onClick={copyMonToWeekend}
                className="mono inline-flex items-center gap-1.5 border border-border px-3 py-1.5 text-[10px] uppercase tracking-widest text-muted-foreground hover:border-[#C9A85F] hover:text-[#8A7333] transition-colors"
              >
                <Copy size={11} /> Copy Mon → Sat &amp; Sun
              </button>
            </div>
          </div>
          <div className="mt-2 border border-border divide-y divide-border">
            {WEEKDAYS.map(({ key, label }) => {
              const d = form.business_hours[key];
              // Only hard-disable the time inputs when the business has
              // explicitly chosen "By appointment" for this day — picking
              // a time doesn't make sense if the schedule is request-only.
              //
              // We DON'T disable on `closed`, even though the day defaults
              // to closed=true for brand-new businesses. If we did, users
              // would see 7 rows of greyed-out time pickers on first load
              // and have to tick + untick "Closed" for every day before
              // they could enter their hours. Instead, the onChange below
              // auto-clears `closed` the moment the user picks a time —
              // so clicking straight into "Mon open" just works.
              const disabled = d.by_appointment;
              return (
                <div
                  key={key}
                  data-testid={`hours-row-${key}`}
                  className="grid grid-cols-[64px_1fr] items-center gap-3 p-3 md:grid-cols-[80px_auto_auto_1fr] md:gap-4"
                >
                  <span className="mono text-[11px] uppercase tracking-widest">
                    {label}
                  </span>
                  <div className="flex items-center gap-2 col-span-1 md:col-span-1">
                    <input
                      type="time"
                      data-testid={`hours-open-${key}`}
                      aria-label={`${label} open time`}
                      value={d.open}
                      disabled={disabled}
                      onChange={(e) => setDay(key, { open: e.target.value, closed: false })}
                      className="mono h-9 w-24 rounded-none border border-border bg-background px-2 text-xs disabled:opacity-40"
                    />
                    <span className="mono text-[10px] text-muted-foreground">to</span>
                    <input
                      type="time"
                      data-testid={`hours-close-${key}`}
                      aria-label={`${label} close time`}
                      value={d.close}
                      disabled={disabled}
                      onChange={(e) => setDay(key, { close: e.target.value, closed: false })}
                      className="mono h-9 w-24 rounded-none border border-border bg-background px-2 text-xs disabled:opacity-40"
                    />
                  </div>
                  <label className="mono inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`hours-closed-${key}`}
                      checked={d.closed}
                      onChange={(e) => setDay(key, {
                        closed: e.target.checked,
                        by_appointment: e.target.checked ? false : d.by_appointment,
                      })}
                      className="h-3.5 w-3.5 accent-[#C9A85F]"
                    />
                    Closed
                  </label>
                  <label className="mono inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">
                    <input
                      type="checkbox"
                      data-testid={`hours-appt-${key}`}
                      checked={d.by_appointment}
                      onChange={(e) => setDay(key, {
                        by_appointment: e.target.checked,
                        closed: e.target.checked ? false : d.closed,
                      })}
                      className="h-3.5 w-3.5 accent-[#C9A85F]"
                    />
                    By appointment
                  </label>
                </div>
              );
            })}
          </div>
        </div>

        {user?.lat != null && user?.lng != null && (
          <div
            data-testid="profile-current-coords"
            className="flex items-center gap-2 border-l-2 border-[#C9A85F] pl-3 text-xs text-muted-foreground"
          >
            <MapPin size={12} className="text-[#C9A85F]" />
            <span className="mono">
              {user.lat.toFixed(4)}, {user.lng.toFixed(4)}
            </span>
          </div>
        )}

        {/*
          Digest cadence — how often the "yesterday's requests" summary
          email lands in the business owner's inbox. Radio group instead
          of a dropdown so the tradeoff is visible at a glance.
        */}
        <div className="grid gap-2">
          <Label className="mono text-[10px] uppercase tracking-[0.2em]">
            Daily digest email
          </Label>
          <p className="mono text-[10px] text-muted-foreground">
            Summary of yesterday's booking requests, delivered at 8am Sydney time.
          </p>
          <div className="mt-1 grid gap-2" data-testid="digest-frequency-group">
            {[
              { key: "daily",  label: "Daily",   caption: "8am every day (skips days with zero requests)" },
              { key: "weekly", label: "Weekly",  caption: "8am Monday — last 7 days rolled up" },
              { key: "off",    label: "Off",     caption: "No digest emails" },
            ].map((opt) => (
              <label
                key={opt.key}
                data-testid={`digest-frequency-${opt.key}`}
                className={`flex items-start gap-3 cursor-pointer border px-4 py-3 transition-colors ${
                  form.digest_frequency === opt.key
                    ? "border-[#C9A85F] bg-[#C9A85F]/5"
                    : "border-border hover:border-foreground/40"
                }`}
              >
                <input
                  type="radio"
                  name="digest_frequency"
                  value={opt.key}
                  checked={form.digest_frequency === opt.key}
                  onChange={() => setForm((f) => ({ ...f, digest_frequency: opt.key }))}
                  className="mt-1 accent-[#C9A85F]"
                />
                <div>
                  <p className="text-sm font-medium">{opt.label}</p>
                  <p className="mono text-[10px] text-muted-foreground">{opt.caption}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {error && (
          <p data-testid="profile-error" className="mono text-xs text-[#F87171]">
            {error}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            type="submit"
            data-testid="profile-save-btn"
            disabled={busy}
            className="gold-metallic-bg px-5 py-3 text-sm font-semibold uppercase tracking-widest text-black active:scale-[0.98] transition-transform disabled:opacity-60"
          >
            {busy ? "Saving…" : "Save profile"}
          </button>
          <Link
            to="/dashboard"
            data-testid="profile-cancel-link"
            className="mono text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground"
          >
            Cancel
          </Link>
        </div>
      </form>
    </div>
  );
}
